import {
  TopExperienceDataDto,
  SessionTrendDataDto,
} from '@repo/shared/dtos/analytics/AnalyticsResponseDto';
import { ChartData } from '../shared/types';
import { dateUtils } from './dateUtils';

export const formatTitle = (title: string, maxLength: number = 20): string => {
  if (!title) return '';
  return title.length > maxLength ? `${title.substring(0, maxLength)}...` : title;
};

export const formatDurationVerbose = (minutes: number): string => {
  const safeMinutes = Number.isFinite(minutes) ? Math.max(0, minutes) : 0;
  const roundedMinutes = Math.round(safeMinutes);

  if (roundedMinutes < 60) {
    return `${roundedMinutes}m`;
  }

  const hours = Math.floor(roundedMinutes / 60);
  const remainingMinutes = roundedMinutes % 60;

  if (remainingMinutes === 0) {
    return `${hours}h`;
  }

  return `${hours}h ${remainingMinutes}m`;
};

export const buildFullTimestamp = (date: Date | string): string => {
  return dateUtils.formatDateTime(date);
};

export const calculateUtilizationRate = (utilized: number, total: number): number => {
  if (!total || total <= 0) return 0;
  return Math.round((utilized / total) * 100);
};

export const formatNumber = (value: number, options?: Intl.NumberFormatOptions): string => {
  if (value === null || value === undefined) return '—';
  return new Intl.NumberFormat('en-US', options).format(value);
};

export const formatPercentage = (value: number, decimalPlaces: number = 1): string => {
  if (value === null || value === undefined) return '—';
  return `${value.toFixed(decimalPlaces)}%`;
};

export const formatDuration = (minutes: number): string => {
  const safe = Number.isFinite(minutes) ? Math.max(0, Math.round(minutes)) : 0;
  return safe.toString();
};

/** Formats a duration in minutes with a trailing "m" unit label. */
export const formatDurationWithUnit = (minutes: number): string => {
  const safe = Number.isFinite(minutes) ? Math.max(0, Math.round(minutes)) : 0;
  return `${safe}m`;
};

export const formatPeriod = (date: Date | string, format: 'month' | 'date' = 'month'): string => {
  const d = typeof date === 'string' ? new Date(date) : date;
  if (format === 'month') return d.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
  return d.toLocaleDateString();
};

export type MetricFormat = 'number' | 'duration' | 'percentage';

export const formatValue = (
  value: number,
  kind: MetricFormat = 'number',
  decimals: number = 1,
): string => {
  switch (kind) {
    case 'duration':
      return formatDurationWithUnit(value);
    case 'percentage':
      return formatPercentage(value, decimals);
    default:
      return formatNumber(value);
  }
};

export const transformTopExperiencesForChart = (
  data: TopExperienceDataDto[],
  options: {
    maxItems?: number;
    titleMaxLength?: number;
    showSessions?: boolean;
    showMinutes?: boolean;
  } = {},
): ChartData[] => {
  const { maxItems = 10, titleMaxLength = 20, showSessions = true } = options;

  return data.slice(0, maxItems).map((item, index) => ({
    id: item.experienceTitle,
    label: formatTitle(item.experienceTitle, titleMaxLength),
    value: showSessions ? item.sessionCount : Math.round(item.totalMinutes),
    fullTitle: item.experienceTitle,
    sessions: item.sessionCount,
    minutes: Math.round(item.totalMinutes),
    percentage: item.percentage,
    rank: index + 1,
  }));
};

export const transformSessionTrendsForChart = (
  data: SessionTrendDataDto[],
  options: {
    showSessions?: boolean;
    showMinutes?: boolean;
    dateFormat?: 'short' | 'long';
  } = {},
): ChartData[] => {
  const { showSessions = true, showMinutes = false, dateFormat = 'short' } = options;
  const result: ChartData[] = [];

  if (showSessions && data.length > 0) {
    data.forEach(item => {
      result.push({
        id: `${item.monthStart}-sessions`,
        label: formatDate(item.monthStart, dateFormat),
        value: item.sessionCount,
        type: 'sessions',
        date: item.monthStart,
      });
    });
  }

  if (showMinutes && data.length > 0) {
    data.forEach(item => {
      result.push({
        id: `${item.monthStart}-minutes`,
        label: formatDate(item.monthStart, dateFormat),
        value: Math.round(item.totalMinutes),
        type: 'minutes',
        date: item.monthStart,
      });
    });
  }

  return result;
};

export const formatDate = (date: Date | string, format: 'short' | 'long' = 'short'): string => {
  const d = typeof date === 'string' ? new Date(date) : date;

  if (!d || isNaN(d.getTime())) {
    return 'Invalid Date';
  }

  if (format === 'long') {
    return d.toLocaleDateString('en-US', {
      month: 'long',
      year: 'numeric',
      timeZone: 'UTC',
    });
  }

  return d.toLocaleDateString('en-US', {
    month: 'short',
    year: 'numeric',
    timeZone: 'UTC',
  });
};

export const createSafeMonthDate = (monthYear: string): Date => {
  const [year, month] = monthYear.split('-').map(Number);
  return new Date(Date.UTC(year, month - 1, 1));
};

export const formatMonthYear = (monthYear: string, format: 'short' | 'long' = 'short'): string => {
  const date = createSafeMonthDate(monthYear);
  return formatDate(date, format);
};

export const calculateChange = (
  current: number,
  previous: number,
): {
  value: number;
  percentage: number;
  direction: 'up' | 'down' | 'neutral';
  formatted: string;
} => {
  if (!previous || previous === 0) {
    return {
      value: current,
      percentage: 0,
      direction: 'neutral',
      formatted: '—',
    };
  }

  const change = current - previous;
  const percentage = (change / previous) * 100;
  const direction = change > 0 ? 'up' : change < 0 ? 'down' : 'neutral';

  return {
    value: change,
    percentage,
    direction,
    formatted: `${change > 0 ? '+' : ''}${formatNumber(change)} (${formatPercentage(percentage)})`,
  };
};

export const validateData = <T>(data: T[] | undefined | null): T[] => {
  if (!data || !Array.isArray(data)) {
    return [];
  }
  return data.filter(item => item !== null && item !== undefined);
};

export const sortData = <T>(
  data: T[],
  sortField: keyof T,
  direction: 'asc' | 'desc' = 'desc',
): T[] => {
  return [...data].sort((a, b) => {
    const aVal = a[sortField];
    const bVal = b[sortField];

    if (typeof aVal === 'number' && typeof bVal === 'number') {
      return direction === 'asc' ? aVal - bVal : bVal - aVal;
    }

    if (typeof aVal === 'string' && typeof bVal === 'string') {
      return direction === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
    }

    return 0;
  });
};

export const groupBy = <T, K extends keyof T>(data: T[], key: K): Record<string, T[]> => {
  return data.reduce(
    (groups, item) => {
      const groupKey = String(item[key]);
      if (!groups[groupKey]) {
        groups[groupKey] = [];
      }
      groups[groupKey].push(item);
      return groups;
    },
    {} as Record<string, T[]>,
  );
};

export const createPagination = (
  totalItems: number,
  currentPage: number,
  pageSize: number,
): {
  totalPages: number;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
  startIndex: number;
  endIndex: number;
} => {
  const totalPages = Math.ceil(totalItems / pageSize);
  const startIndex = (currentPage - 1) * pageSize;
  const endIndex = Math.min(startIndex + pageSize, totalItems);

  return {
    totalPages,
    hasNextPage: currentPage < totalPages,
    hasPreviousPage: currentPage > 1,
    startIndex,
    endIndex,
  };
};
